export interface YearGroupFinalizationStatus {
  id: string;
  yearGroup: string;
  displayName: string;
  status: 'pending' | 'ready' | 'finalized' | 'blocked';
  lessonCount: number;
  conflictCount: number;
  coveragePercentage: number;
  teacherSatisfaction: number;
  canFinalize: boolean;
  blockedReason?: string;
  finalizedAt?: string;
  finalizedBy?: string;
  finalizedByName?: string;
  lastModified: string;
}

export interface FinalizationChecklist {
  id: string;
  category: string;
  items: {
    id: string;
    label: string;
    status: 'passed' | 'failed' | 'warning';
    details?: string;
    affectedCount?: number;
  }[];
}

export interface FinalizationStats {
  totalYearGroups: number;
  finalizedCount: number;
  pendingCount: number;
  blockedCount: number;
  overallProgress: number;
  estimatedCompletion?: string;
}

export const mockYearGroupStatus: YearGroupFinalizationStatus[] = [
  {
    id: 'year-7-status',
    yearGroup: 'year-7',
    displayName: 'Year 7',
    status: 'ready',
    lessonCount: 42,
    conflictCount: 0,
    coveragePercentage: 100,
    teacherSatisfaction: 94,
    canFinalize: true,
    lastModified: '2025-01-16T10:00:00Z',
  },
  {
    id: 'year-8-status',
    yearGroup: 'year-8',
    displayName: 'Year 8',
    status: 'ready',
    lessonCount: 44,
    conflictCount: 0,
    coveragePercentage: 100,
    teacherSatisfaction: 91,
    canFinalize: true,
    lastModified: '2025-01-16T09:30:00Z',
  },
  {
    id: 'year-9-status',
    yearGroup: 'year-9',
    displayName: 'Year 9',
    status: 'blocked',
    lessonCount: 40,
    conflictCount: 2,
    coveragePercentage: 98,
    teacherSatisfaction: 87,
    canFinalize: false,
    blockedReason: '2 unresolved conflicts in PE sessions',
    lastModified: '2025-01-16T11:00:00Z',
  },
  {
    id: 'year-10-status',
    yearGroup: 'year-10',
    displayName: 'Year 10',
    status: 'finalized',
    lessonCount: 48,
    conflictCount: 0,
    coveragePercentage: 100,
    teacherSatisfaction: 95,
    canFinalize: false,
    finalizedAt: '2025-01-15T14:30:00Z',
    finalizedBy: 'admin-1',
    finalizedByName: 'John Smith',
    lastModified: '2025-01-15T14:30:00Z',
  },
  {
    id: 'year-11-status',
    yearGroup: 'year-11',
    displayName: 'Year 11',
    status: 'finalized',
    lessonCount: 50,
    conflictCount: 0,
    coveragePercentage: 100,
    teacherSatisfaction: 92,
    canFinalize: false,
    finalizedAt: '2025-01-15T14:35:00Z',
    finalizedBy: 'admin-1',
    finalizedByName: 'John Smith',
    lastModified: '2025-01-15T14:35:00Z',
  },
  {
    id: 'year-12-status',
    yearGroup: 'year-12',
    displayName: 'Year 12',
    status: 'pending',
    lessonCount: 38,
    conflictCount: 0,
    coveragePercentage: 96,
    teacherSatisfaction: 89,
    canFinalize: false,
    blockedReason: 'Curriculum coverage below 100%',
    lastModified: '2025-01-16T08:00:00Z',
  },
  {
    id: 'year-13-status',
    yearGroup: 'year-13',
    displayName: 'Year 13',
    status: 'pending',
    lessonCount: 36,
    conflictCount: 1,
    coveragePercentage: 95,
    teacherSatisfaction: 86,
    canFinalize: false,
    blockedReason: '1 conflict, curriculum coverage below 100%',
    lastModified: '2025-01-16T08:15:00Z',
  },
];

export const mockFinalizationChecklists: FinalizationChecklist[] = [
  {
    id: 'checklist-conflicts',
    category: 'Conflict Resolution',
    items: [
      { id: 'c1', label: 'No teacher double-bookings', status: 'passed' },
      { id: 'c2', label: 'No room double-bookings', status: 'passed' },
      { id: 'c3', label: 'No student group overlaps', status: 'warning', details: '3 soft conflicts', affectedCount: 3 },
      { id: 'c4', label: 'All hard rules satisfied', status: 'passed' },
    ],
  },
  {
    id: 'checklist-coverage',
    category: 'Curriculum Coverage',
    items: [
      { id: 'cv1', label: 'Core subjects allocated (Years 7-11)', status: 'passed' },
      { id: 'cv2', label: 'Option subjects allocated (Years 12-13)', status: 'warning', details: '96% coverage', affectedCount: 4 },
      { id: 'cv3', label: 'PE requirements met', status: 'failed', details: 'Year 9 missing 1 session', affectedCount: 1 },
      { id: 'cv4', label: 'Assembly slots reserved', status: 'passed' },
    ],
  },
  {
    id: 'checklist-resources',
    category: 'Resource Allocation',
    items: [
      { id: 'r1', label: 'All lessons have assigned rooms', status: 'passed' },
      { id: 'r2', label: 'Specialist rooms properly allocated', status: 'passed' },
      { id: 'r3', label: 'Teacher workload within limits', status: 'warning', details: '2 teachers at 100%', affectedCount: 2 },
      { id: 'r4', label: 'Room capacities respected', status: 'passed' },
    ],
  },
  {
    id: 'checklist-preferences',
    category: 'Preferences & Quality',
    items: [
      { id: 'p1', label: 'Teacher preferences satisfied (>85%)', status: 'passed', details: '89% average' },
      { id: 'p2', label: 'Distribution score acceptable (>80%)', status: 'passed', details: '92% score' },
      { id: 'p3', label: 'No consecutive teaching overruns', status: 'warning', details: '1 teacher has 6 consecutive', affectedCount: 1 },
      { id: 'p4', label: 'Break times properly distributed', status: 'passed' },
    ],
  },
];

export const statusConfig: Record<string, { color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; label: string; bgColor: string }> = {
  pending: { color: 'default', label: 'Pending Review', bgColor: '#f5f5f5' },
  ready: { color: 'success', label: 'Ready to Finalize', bgColor: '#e8f5e9' },
  finalized: { color: 'primary', label: 'Finalized', bgColor: '#e3f2fd' },
  blocked: { color: 'error', label: 'Blocked', bgColor: '#ffebee' },
};

export const getFinalizationStats = (): FinalizationStats => {
  const finalized = mockYearGroupStatus.filter((y) => y.status === 'finalized').length;
  const blocked = mockYearGroupStatus.filter((y) => y.status === 'blocked').length;
  const pending = mockYearGroupStatus.filter((y) => y.status === 'pending').length;
  const total = mockYearGroupStatus.length;

  return {
    totalYearGroups: total,
    finalizedCount: finalized,
    pendingCount: pending,
    blockedCount: blocked,
    overallProgress: Math.round((finalized / total) * 100),
    estimatedCompletion: blocked > 0 ? undefined : '2025-01-20',
  };
};

export const getChecklistSummary = () => {
  let passed = 0;
  let failed = 0;
  let warnings = 0;

  mockFinalizationChecklists.forEach((checklist) => {
    checklist.items.forEach((item) => {
      if (item.status === 'passed') passed++;
      else if (item.status === 'failed') failed++;
      else if (item.status === 'warning') warnings++;
    });
  });

  return { passed, failed, warnings, total: passed + failed + warnings };
};

